import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConsultationSnapshot } from '../utils/consultationSnapshot';
import type { VitalSignsState } from '@components/Prescription/VitalSignsSection';

const PREFIX = 'rx-draft:';
// Drafts hold patient data on a possibly shared clinic computer: keep them short-lived.
const TTL_MS = 12 * 60 * 60 * 1000;

export interface PrescriptionDraft {
  savedAt: number;
  patientId: string;
  snapshot: ConsultationSnapshot;
  vitalSigns?: VitalSignsState;
}

const safeGet = (key: string): PrescriptionDraft | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw) as PrescriptionDraft;
    if (Date.now() - draft.savedAt > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

const safeRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

/** Removes every prescription draft stored in this browser (called on logout). */
export const clearAllPrescriptionDrafts = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage unavailable */
  }
};

/**
 * Autosaves the in-progress consultation (debounced) so a refresh, crash or accidental
 * navigation doesn't lose it, and warns before leaving with unsaved changes.
 *
 * @param key   unique per doctor + appointment/patient; null disables autosave
 * @param draft current form content, or null when there is nothing worth saving
 */
export function usePrescriptionDraft(key: string | null, draft: Omit<PrescriptionDraft, 'savedAt'> | null) {
  const storageKey = key ? PREFIX + key : null;
  const [pendingRestore, setPendingRestore] = useState<PrescriptionDraft | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  // Don't overwrite a stored draft before the user has chosen to restore or discard it
  const decidedRef = useRef(false);

  useEffect(() => {
    decidedRef.current = false;
    setLastSavedAt(null);
    const existing = storageKey ? safeGet(storageKey) : null;
    setPendingRestore(existing);
    if (!existing) decidedRef.current = true;
  }, [storageKey]);

  const serialized = draft ? JSON.stringify(draft) : null;

  useEffect(() => {
    if (!storageKey || !decidedRef.current) return;
    const timer = setTimeout(() => {
      try {
        if (serialized) {
          const savedAt = Date.now();
          localStorage.setItem(storageKey, JSON.stringify({ ...JSON.parse(serialized), savedAt }));
          setLastSavedAt(savedAt);
        } else {
          localStorage.removeItem(storageKey);
          setLastSavedAt(null);
        }
      } catch {
        /* storage full or unavailable: autosave is best-effort */
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [storageKey, serialized, pendingRestore]);

  const hasUnsavedWork = !!serialized;
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedWork]);

  const acceptRestore = useCallback(() => {
    decidedRef.current = true;
    const restored = pendingRestore;
    setPendingRestore(null);
    return restored;
  }, [pendingRestore]);

  const discardRestore = useCallback(() => {
    decidedRef.current = true;
    if (storageKey) safeRemove(storageKey);
    setPendingRestore(null);
  }, [storageKey]);

  /** Call after the prescription is saved. */
  const clear = useCallback(() => {
    if (storageKey) safeRemove(storageKey);
    setLastSavedAt(null);
  }, [storageKey]);

  return { pendingRestore, acceptRestore, discardRestore, clear, lastSavedAt };
}
